import { apiClient } from "@/shared/api/client";
import { primaryMotif } from "@/shared/utils/motifs";
import type { AnalyzedMove, GameAnalysis } from "@chessla/shared";
import { applySanMove, legalBestMove } from "@chessla/shared";

export type AnalyzedMoveResult = AnalyzedMove;
export type GameAnalysisResult = GameAnalysis;

const withCompleteMotifs = (move: AnalyzedMoveResult): AnalyzedMoveResult => {
  if (
    move.bestMoveSan === move.san &&
    legalBestMove(move) &&
    applySanMove(move.fenBefore, move.san)?.fen === move.fenAfter
  ) {
    return {
      ...move,
      centipawnLoss: 0,
      isBlunder: false,
      isMistake: false,
      isImprecision: false,
      motifs: [
        move.san.endsWith("#") ? "checkmate" : "best",
        ...(move.motifs ?? []).filter(
          (motif) =>
            ![
              "checkmate",
              "book",
              "brilliant",
              "best",
              "excellent",
              "good",
              "blunder",
              "mistake",
              "imprecision",
              "opponent_blunder",
              "opponent_mistake",
              "opponent_imprecision",
            ].includes(motif),
        ),
      ],
      principleSignals: move.principleSignals ?? [],
    };
  }
  const motifs = new Set(move.motifs ?? []);
  // Legacy book tags were inferred from move number, without theory evidence.
  motifs.delete("book");
  const hasQuality = ["checkmate", "brilliant", "book", "best", "excellent", "good"].some((tag) => motifs.has(tag));
  const hasError = [
    "blunder",
    "mistake",
    "imprecision",
    "opponent_blunder",
    "opponent_mistake",
    "opponent_imprecision",
  ].some((tag) => motifs.has(tag));

  // Older analyses classified opponent errors only. Fill in the symmetric
  // quality/context annotations from the stored move measurements.
  if (
    !hasQuality &&
    !hasError &&
    (move.bestMoveSan === move.san || (move.evalBeforeCp != null && move.evalAfterCp != null))
  ) {
    const isExactBest = move.bestMoveSan != null && move.san === move.bestMoveSan;
    if (isExactBest) motifs.add("best");
    else if (move.centipawnLoss < 50) {
      if (move.centipawnLoss <= 9) motifs.add("excellent");
      else motifs.add("good");
    } else if (move.centipawnLoss >= 200) {
      motifs.add(move.isPlayerMove ? "blunder" : "opponent_blunder");
    } else if (move.centipawnLoss >= 100) {
      motifs.add(move.isPlayerMove ? "mistake" : "opponent_mistake");
    } else {
      motifs.add(move.isPlayerMove ? "imprecision" : "opponent_imprecision");
    }
  }

  if (move.clockSeconds != null && move.clockSeconds < 30) {
    motifs.add("time_pressure");
  } else if (
    move.clockSeconds != null &&
    move.timeSpentSeconds != null &&
    move.clockSeconds > 0 &&
    move.timeSpentSeconds / (move.clockSeconds + move.timeSpentSeconds) > 0.5
  ) {
    motifs.add("low_time");
  }

  const motif = primaryMotif(motifs);
  return {
    ...move,
    motifs: motif ? [motif, ...[...motifs].filter((value) => value !== motif)] : [...motifs],
    principleSignals: move.principleSignals ?? [],
  };
};

/**
 * Fetches a specific game's full Stockfish analysis, including every
 * one of the player's own moves (not just the single critical moment
 * Nikola's chat context is capped to) — used by MoveNavigator to let
 * the player step through an entire game move-by-move on the board.
 */
export const fetchGameAnalysis = (gameId: string, signal?: AbortSignal): Promise<GameAnalysisResult> =>
  apiClient<GameAnalysisResult>(`/engine/analysis/${gameId}`, { signal }).then((analysis) => ({
    ...analysis,
    moves: analysis.moves.map(withCompleteMotifs),
  }));
